import { create } from "zustand";
import { devtools, persist } from "zustand/middleware";
import { CompanyUser, QualityCheck } from "../supabase/types";

type OperatingSystem = "windows" | "mac" | "other";

interface AppState {
  // Selected company state
  selectedCompanyId: string | null;
  selectedCompanyUser: CompanyUser | null;

  // Current quality check state
  currentQualityCheck: QualityCheck | null;

  // UI state
  operatingSystem: OperatingSystem | null;
  currentPageHasUnsavedChanges: boolean;
  isInitialized: boolean;

  // Actions
  setSelectedCompany: (companyId: string | null, companyUser: CompanyUser | null) => void;
  setCurrentQualityCheck: (qualityCheck: QualityCheck | null) => void;
  setOperatingSystem: (os: OperatingSystem) => void;
  setCurrentPageHasUnsavedChanges: (hasChanges: boolean) => void;
  setInitialized: (initialized: boolean) => void;
  clearAppState: () => void;
}

// Helper to detect operating system
const detectOperatingSystem = (): OperatingSystem => {
  if (typeof navigator === "undefined") return "other";

  const platform = navigator.platform.toLowerCase();

  if (platform.includes("win")) {
    return "windows";
  } else if (platform.includes("mac")) {
    return "mac";
  } else {
    return "other";
  }
};

export const useAppStore = create<AppState>()(
  devtools(
    persist(
      (set) => ({
        // Initial state
        selectedCompanyId: null,
        selectedCompanyUser: null,
        currentQualityCheck: null,
        operatingSystem: null,
        currentPageHasUnsavedChanges: false,
        isInitialized: false,

        // Actions
        setSelectedCompany: (companyId, companyUser) => {
          set({
            selectedCompanyId: companyId,
            selectedCompanyUser: companyUser,
          })
        },

        setCurrentQualityCheck: (qualityCheck) => set({ currentQualityCheck: qualityCheck }),

        setOperatingSystem: (os) => set({ operatingSystem: os }),

        setCurrentPageHasUnsavedChanges: (hasChanges) => set({
          currentPageHasUnsavedChanges: hasChanges
        }),

        setInitialized: (initialized) => set({ isInitialized: initialized }),

        clearAppState: () => set({
          selectedCompanyId: null,
          selectedCompanyUser: null,
          currentQualityCheck: null,
          currentPageHasUnsavedChanges: false,
          // Keep OS and initialized state
        }),
      }),
      {
        name: "app-store",
        partialize: (state) => ({
          // Only persist selected company and OS
          selectedCompanyId: state.selectedCompanyId,
          operatingSystem: state.operatingSystem,
        }),
      }
    )
  )
);

// Initialize operating system on first load
if (typeof window !== "undefined") {
  const os = detectOperatingSystem();
  useAppStore.getState().setOperatingSystem(os);
}